import { useMemo, useState } from "react";

import { cn } from "@/lib/utils";
import { Spinner } from "@fluentui/react-components";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, PlusCircle, Search } from "lucide-react";
import PreprocessIcon from "@/assets/preprocess_icon.svg";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import FilePond from "@/components/ui/file-pond";

import CompaniesTable from "./companies-table";

import { useAdminCompaniesQuery } from "@/queries/admin/companies";
import {
  adminCreateCompanyFormSchema,
  AdminCreateCompanyFormSchema,
  useCreateCompany,
} from "@/mutations/admin/create-company";

const Companies = () => {
  const [searchString, setSearchString] = useState("");

  const { adminCompanies, adminCompaniesLoading } = useAdminCompaniesQuery();

  const companies = useMemo(() => {
    if (searchString) {
      const filterString = searchString.toLowerCase();

      return adminCompanies?.filter((company) =>
        company.name?.toLowerCase().includes(filterString),
      );
    } else {
      return adminCompanies;
    }
  }, [adminCompanies, searchString]);

  const count = useMemo(() => companies?.length, [companies]);

  if (adminCompaniesLoading) {
    return (
      <div>
        <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
          Companies
        </h3>
        <div className="w-full flex-col justify-around align-middle">
          <Spinner size="huge" />
          <p className="text-center text-base font-medium text-[#727E94]">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="py-4 text-2xl font-semibold group-hover:underline text-left">
        Companies
      </h3>
      <div className="w-full flex flex-col gap-4 items-start self-stretch">
        <div className="flex flex-col md:flex-row md:items-center justify-between self-stretch">
          <div className="flex flex-row items-center gap-2">
            <p className="text-lg text-[#181d27] font-semibold">
              {`${count} ${count === 1 ? "Company" : "Companies"}`}
            </p>
            <CreateCompanyForm />
          </div>
          <div className="flex flex-row items-center gap-3">
            <div
              className={cn(
                "flex flex-row items-center gap-0.5",
                "border-b border-[#57576B] transition-colors focus-within:border-black",
              )}
            >
              <Search />
              <input
                type="text"
                className={cn(
                  "flex h-10 w-full min-w-48 px-3 py-2 text-base",
                  "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                )}
                placeholder="Search for any company"
                value={searchString}
                onChange={(ev) => {
                  ev.preventDefault();
                  setSearchString(ev.target.value);
                }}
              />
            </div>
          </div>
        </div>
        <CompaniesTable companies={companies} />
      </div>
    </div>
  );
};

const CreateCompanyForm = () => {
  const [open, setOpen] = useState(false);

  const { createCompany, isCreatingCompany } = useCreateCompany();

  const form = useForm<AdminCreateCompanyFormSchema>({
    resolver: zodResolver(adminCreateCompanyFormSchema),
    defaultValues: {
      name: "",
      linkedIn: "",
      websiteLink: "",
      companyLogo: undefined,
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <PlusCircle />
          Add A Company
        </Button>
      </DialogTrigger>
      <DialogContent
        className="w-full p-0 sm:max-w-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <DialogHeader className="p-6">
          <div className="flex items-center gap-2">
            <img src={PreprocessIcon} alt="Preprocess Icon" />
            <DialogTitle>Add a Company</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Enter the details of the company here and click &quot;Save&quot;
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh] px-6 pb-6">
          <Form {...form}>
            <form className="space-y-4 w-full">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Company Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Acme" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="companyLogo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Logo</FormLabel>
                    <FormControl>
                      <FilePond
                        value={field.value ? [field.value] : []}
                        accept="image/*"
                        onChange={(files) => {
                          field.onChange(files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="linkedIn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>LinkedIn</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://www.linkedin.com/..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="websiteLink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Website</FormLabel>
                    <FormControl>
                      <Input placeholder="https://www.acme.com/" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex flex-row justify-end">
                <Button
                  disabled={!form.formState.isValid || isCreatingCompany}
                  onClick={() =>
                    void form.handleSubmit((data) => createCompany(data))()
                  }
                >
                  {isCreatingCompany && <Loader2 className="animate-spin" />}
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default Companies;
